import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Barcode, ArrowRight } from "lucide-react";

export default function Register() {
  const { register, error } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await register(email, password, name);
    setLoading(false);
    if (ok) {
      // If user came from /pricing with a chosen plan, redirect to checkout of that plan
      const VALID_TIERS = new Set(["micro", "start", "pro", "business", "enterprise"]);
      const raw = searchParams.get("plan") ||
        (() => { try { return sessionStorage.getItem("pendingPlan"); } catch { return null; } })();
      const pendingPlan = raw && VALID_TIERS.has(raw) ? raw : null;
      try { sessionStorage.removeItem("pendingPlan"); } catch (_) {}
      navigate(pendingPlan ? `/checkout/${pendingPlan}` : "/publish");
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F8F9FA]">
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[#09090B] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1533135091724-62cc5402aa20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjBsaW5lcyUyMHBhdHRlcm58ZW58MHx8fHwxNzgzMTM1MjI2fDA&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(100%) contrast(1.1)",
          }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-2 font-display font-bold text-xl">
            <Barcode className="w-6 h-6" />
            <span>MAGIC SIP</span>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <div className="eyebrow text-white/70">Criar conta</div>
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight">
            Automatize o cadastramento de produtos no seu{" "}
            <span style={{ color: "#7AA2FF" }}>e-commerce</span>
          </h1>
          <p className="text-white/70 max-w-md leading-relaxed text-sm">
            Envie uma planilha com até <strong className="text-white">10.000 produtos</strong> apenas
            com <strong className="text-white">Nome, Categoria e Preço</strong>. A IA faz todo o resto —
            busca fotos, descrição, indicação de uso e SEO automaticamente.
          </p>
          <p className="text-white/60 max-w-md leading-relaxed text-xs">
            Tenha seu cadastro completo em <strong className="text-white">poucas horas</strong> ao
            invés de perder dias — ou meses — preenchendo manualmente.
          </p>
        </div>
        <div className="relative z-10 text-xs text-white/50 font-mono">
          v1.0 · UTILITÁRIO PARA E-COMMERCE
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="eyebrow mb-3">Registro</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#09090B] mb-2">
            Criar sua conta
          </h2>
          <p className="text-sm text-zinc-500 mb-8">
            Já tem conta?{" "}
            <Link to="/login" className="brand-text font-medium hover:underline" data-testid="link-to-login">
              Entrar
            </Link>
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <Label htmlFor="name" className="eyebrow">Nome</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Seu nome"
                className="mt-2 h-11 rounded-md border-zinc-300 focus-visible:ring-2 focus-visible:ring-[#002FA7]"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email" className="eyebrow">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@empresa.com"
                className="mt-2 h-11 rounded-md border-zinc-300 focus-visible:ring-2 focus-visible:ring-[#002FA7]"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="eyebrow">Senha (mín. 6)</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 h-11 rounded-md border-zinc-300 focus-visible:ring-2 focus-visible:ring-[#002FA7]"
                data-testid="register-password-input"
              />
            </div>

            {error && (
              <div
                className="text-sm rounded-md border border-red-200 bg-red-50 text-red-700 px-3 py-2"
                data-testid="register-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-md brand-primary text-white font-medium transition-colors"
              data-testid="register-submit-button"
            >
              {loading ? "Criando..." : "Criar conta"}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
